import { Injectable } from '@angular/core';

import { toGcdmRegionCode } from '../../../../utils/province-code';
import { CustomerAddress, CustomerMainType } from '../../../model/customer-information.model';
import { Customer } from '../../../model/customer.model';
import { ExtendedCustomer } from '../../../model/extended-customer.model';
import { GcdmCreateCustomerAddressInput, GcdmCreateCustomerInput } from '../model/create-customer-request.model';

/**
 * Builds the payload of `POST gcdm/customers` from CDF's customer models.
 *
 * Two entry points, because the two flows of CUSTAPP-611 start from different models:
 * the creation flow holds a `Customer` (A1), the profile sync flow an `ExtendedCustomer` (B1).
 */
@Injectable({
  providedIn: 'root',
})
export class GcdmCreateCustomerRequestMapper {
  /** Customer creation flow (case A1): a brand-new customer that does not exist anywhere yet. */
  mapCustomer(customer: Customer): GcdmCreateCustomerInput {
    const { firstName, lastName } = this.requireName(customer.firstName, customer.lastName);

    return {
      // CDF only ever creates private individuals; companies are created in Retail CRM only.
      customerMainType: CustomerMainType.INDIVIDUAL,
      salutation: customer.salutation,
      title: customer.title,
      firstName,
      middleName: customer.middleName,
      lastName,
      suffix: customer.suffix,
      // The dropdown cannot be cleared, so a blank language only comes from source data;
      // send nothing rather than "" (rejected by the backend's @Size(min = 2)).
      correspondenceLanguage: customer.correspondenceLanguage?.trim().toLowerCase() || undefined,
      privateAddress: this.mapAddress(customer.privateContact.address),
      businessAddress: this.mapAddress(customer.businessContact.address),
      personEmail: customer.privateContact.email,
      emailBusiness: customer.businessContact.email,
      phone: customer.privateContact.phone,
      personMobilePhone: customer.privateContact.mobile,
      phoneBusiness: customer.businessContact.phone,
      mobilePhoneBusiness: customer.businessContact.mobile,
      taxNumber: customer.taxNumber,
      taxNumberCountry: customer.taxNumberCountry,
      nationalId1: customer.nationalId1,
      nationalId1DocumentType: customer.nationalId1DocumentType,
    };
  }

  /**
   * Profile edit flow (CUSTAPP-613): an existing customer that is already synced to the NSC
   * Database and whose master data now has to be written there instead of to Retail CRM.
   *
   * The payload is byte-for-byte the creation one - the backend accepts the same body for POST
   * and PUT - so this delegates rather than duplicates. It exists as its own entry point because
   * the *meaning* of the values differs: on create a blank field is noise and is dropped, on
   * update it is the user's deletion and is written as "". That distinction is implemented in the
   * backend mapper; here it is enough that the dialog's empty strings are passed through verbatim
   * and not normalised to undefined.
   *
   * `mainAddress` is deliberately absent: `Customer` does not model it, and the edit dialog cannot
   * change it. The backend keeps whichever address GCDM currently prefers.
   */
  mapCustomerForUpdate(customer: Customer): GcdmCreateCustomerInput {
    return this.mapCustomer(customer);
  }

  /** Profile sync flow (CUSTAPP-611 - case B1): a local Retail CRM customer that has no NSC Database record yet. */
  mapExtendedCustomer(customer: ExtendedCustomer): GcdmCreateCustomerInput {
    const { firstName, lastName } = this.requireName(customer.firstName, customer.lastName);

    return {
      // Passed through rather than forced to INDIVIDUAL, so the backend guard can reject
      // companies with a precise error instead of silently creating a person in GCDM.
      customerMainType: customer.customerMainType ?? CustomerMainType.INDIVIDUAL,
      salutation: customer.salutation,
      title: customer.title,
      firstName,
      middleName: customer.middleName,
      lastName,
      suffix: customer.suffix,
      correspondenceLanguage: customer.correspondenceLanguage?.toLowerCase(),
      mainAddress: customer.mainAddress,
      privateAddress: this.mapAddress(customer.privateAddress),
      businessAddress: this.mapAddress(customer.businessAddress),
      personEmail: customer.emailPrivate,
      emailBusiness: customer.emailBusiness,
      phone: customer.phonePrivate,
      personMobilePhone: customer.mobilePrivate,
      phoneBusiness: customer.phoneBusiness,
      mobilePhoneBusiness: customer.mobileBusiness,
      taxNumber: customer.taxNumber,
      taxNumberCountry: customer.taxNumberCountry,
      nationalId1: customer.nationalId1,
      nationalId1DocumentType: customer.nationalId1DocumentType,
    };
  }

  private mapAddress(address: CustomerAddress | undefined): GcdmCreateCustomerAddressInput | undefined {
    if (!address) {
      return undefined;
    }

    return {
      street: address.street,
      houseNumber: address.houseNumber,
      postalCode: address.postalCode,
      city: address.city,
      // Never send a blank country: "" fails the backend's @Size(min = 2, max = 2) with a 400
      // (CUSTAPP-814). Absent is safe - an address left with no values at all is deleted in GCDM,
      // and GCDM's country is a value-list field that cannot be cleared to "" anyway.
      country: address.country?.trim() || undefined,
      province: toGcdmRegionCode(address.province, address.country),
      addressLine1: address.addressLine1,
      addressLine2: address.addressLine2,
      postOfficeBox: address.postBox,
    };
  }

  private requireName(firstName: string | undefined, lastName: string | undefined) {
    if (!firstName || !lastName) {
      throw new Error(
        `#CDFTF0264 Failed mapping customer to GcdmCreateCustomerInput, as required fields are undefined. ` +
          `Required fields: firstName: ${firstName}, lastName: ${lastName}`,
      );
    }
    return { firstName, lastName };
  }
}
